import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PinInput } from '@realtyka/real-ui';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EnableMfaRequestMfaTypeEnum,
  MfaControllerApi,
} from '../../../openapi/keymaker';
import ErrorService from '../../../services/ErrorService';
import { fetchKeymakerCurrentUser, mfaSignIn } from '../../../slices/AuthSlice';
import { useAppDispatch } from '../../../slices/store';
import { showSuccessToast } from '../../../slices/ToastNotificationSlice';
import { getCommonsApiErrorMessage } from '../../../utils/ErrorUtils';
import { handleUnauthorizedOnly } from '../../../utils/MfaErrorHandlingUtils';
import { getKeymakerConfiguration } from '../../../utils/OpenapiConfigurationUtils';
import { formatPhoneNumber } from '../../../utils/StringUtils';

interface MfaPinCardProps {
  verificationPhoneNumber?: string;
  isForceMfa?: boolean;
  onPinVerified: () => void;
  redirectTo?: string;
  isAgentContext?: boolean;
}

const MfaPinCard: React.FC<MfaPinCardProps> = ({
  verificationPhoneNumber,
  isForceMfa,
  onPinVerified,
  redirectTo,
  isAgentContext = false,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const submitCode = async (code: string) => {
    setIsLoading(true);
    setErrorMessage(undefined);
    const codeNumber = parseInt(code);
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).enableMfa({
        code: codeNumber,
        mfaType: EnableMfaRequestMfaTypeEnum.Sms,
        phoneNumber: verificationPhoneNumber,
      });
      if (isForceMfa) {
        await dispatch(mfaSignIn(codeNumber));
      }
      await dispatch(fetchKeymakerCurrentUser());
      onPinVerified();
      dispatch(
        showSuccessToast('Successfully enabled two factor authentication.'),
      );
    } catch (e: any) {
      // Handle authentication errors (401 Unauthorized only)
      const handled = await handleUnauthorizedOnly({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }

      // Handle other errors (including 403 Forbidden)
      setErrorMessage(getCommonsApiErrorMessage(e));
      ErrorService.notify('Unable to enable two factor authentication', e, {
        data: {
          code: codeNumber,
          mfaType: EnableMfaRequestMfaTypeEnum.Sms,
          phoneNumber: verificationPhoneNumber,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-1 items-start self-stretch mb-4 mt-6'>
      <div className='text-ellipsis font-inter-bold text-[15px] leading-[22px]'>
        Enter authentication code
      </div>
      <div className='font-inter-regular text-[15px] leading-[22px]'>
        We just sent you a 6-digit code to{' '}
        {formatPhoneNumber(verificationPhoneNumber)}
      </div>
      <div className='flex py-2 justify-center items-center self-stretch'>
        <PinInput
          onComplete={(code) => {
            hiddenInputRef.current?.focus();
            submitCode(code);
          }}
          error={errorMessage}
          data-testid='pin-input'
          disabled={isLoading}
        />
      </div>
      {errorMessage && (
        <div className='flex py-3 px-4 justify-center items-center gap-2 self-stretch rounded-[10px] bg-reskin-red-100'>
          <FontAwesomeIcon
            icon={faCircleInfo}
            className='text-reskin-red-600 text-sm md:text-base'
          />
          <div className='text-reskin-red-600 font-inter-medium text-sm md:text-base'>
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default MfaPinCard;
